/**
 * BM-H3775 (Vanessa, 6 Oct 2026: "the snippet of the actual pipeline ... could
 * be visually much clearer"; then, of this track, "put little capitals above
 * each segment ... the pipeline generally can be updated to this").
 *
 * The pipeline drew all seven stages as bordered pills on every row,
 * wrapping onto two lines, nearly all of them green ticks. This is the
 * replacement, on the dashboard and on the Pipeline Tracker page: one thin
 * segment per stage on a single line, grouped under the five golden rule
 * steps (CONFLICT, OB, AML, FORF/RA, CCL), and the one stage that matters
 * named underneath. Done stages are green, the current stage is amber (red
 * when stuck), and stages still to do are faint.
 *
 * Statuses come from pipelineStageStatus, the same answer the stepper uses
 * (BM-H284), so the track can never disagree with it. A segment that is not
 * still to do opens its stage, as the pills did, through `onOpen`.
 */
// The five steps Vanessa named, over the seven tracked stages.
export const TRACK_GROUPS = [
  { label: "CONFLICT", keys: ["conflict"] },
  { label: "OB", keys: ["ob_sent", "ob_returned"] },
  { label: "AML", keys: ["aml_check"] },
  { label: "FORF/RA", keys: ["forf_draft"] },
  { label: "CCL", keys: ["ccl_draft", "ccl_signed"] },
];

// Vanessa, 6 Oct 2026: "can you colour the completed segments in green?"
// Green is a status colour, and done is a status.
const SEGMENT = {
  done: "#1aab6e",
  active: "var(--amber)",
  stuck: "var(--red)",
  todo: "rgba(255,255,255,0.08)",
};
const GROUP_LABEL = {
  active: "var(--amber)",
  stuck: "var(--red)",
  done: "var(--text-muted)",
  todo: "var(--text-faint)",
};
const LABEL = {
  active: "text-status-amber",
  stuck: "text-status-red",
  done: "text-slate-400",
};

export function currentStage(stages, statuses) {
  const stuck = stages.findIndex((s) => statuses[s.key] === "stuck");
  if (stuck > -1) return stuck;
  const active = stages.findIndex((s) => statuses[s.key] === "active");
  if (active > -1) return active;
  // Everything done: the last stage is the one to name.
  return stages.every((s) => statuses[s.key] === "done") ? stages.length - 1 : 0;
}

/** A group reads as its most pressing stage: stuck, then active, then done. */
export function groupStatus(keys, statuses) {
  const st = keys.map((k) => statuses[k] || "todo");
  if (st.includes("stuck")) return "stuck";
  if (st.includes("active")) return "active";
  if (st.every((s) => s === "done")) return "done";
  return st.includes("done") ? "active" : "todo";
}

export default function PipelineStageTrack({ stages, statuses, onOpen }) {
  const byKey = Object.fromEntries(stages.map((s) => [s.key, s]));
  const idx = currentStage(stages, statuses);
  const cur = stages[idx];
  const curStatus = statuses[cur?.key] || "todo";
  const allDone = stages.every((s) => statuses[s.key] === "done");
  const name = (s) => s.label || s.shortLabel;
  const groups = TRACK_GROUPS.map((g) => ({ ...g, keys: g.keys.filter((k) => byKey[k]) }))
    .filter((g) => g.keys.length);
  return (
    <div className="min-w-0" data-testid="pipeline-stage-track">
      <div className="flex gap-[5px]">
        {groups.map((g) => (
          <div key={g.label} className="flex-1 min-w-0">
            <div
              className="text-[8px] font-bold tracking-wider leading-none mb-[3px] truncate"
              style={{ color: GROUP_LABEL[groupStatus(g.keys, statuses)] }}
            >
              {g.label}
            </div>
            <div className="flex gap-[2px]">
              {g.keys.map((k) => {
                const st = statuses[k] || "todo";
                const clickable = !!onOpen && st !== "todo";
                return (
                  <span
                    key={k}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    title={`${name(byKey[k])}: ${st === "todo" ? "to do" : st}${clickable ? " (click to open)" : ""}`}
                    onClick={clickable ? (e) => { e.stopPropagation(); onOpen(k); } : undefined}
                    onKeyDown={clickable ? (e) => { if (e.key === "Enter") { e.stopPropagation(); onOpen(k); } } : undefined}
                    className={`h-1.5 flex-1 rounded-full ${clickable ? "cursor-pointer hover:opacity-80" : ""}`}
                    style={{ background: SEGMENT[st] || SEGMENT.todo }}
                    data-testid={`pipeline-stage-segment-${k}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-1 text-[11px] leading-tight truncate">
        <span className={`font-semibold ${LABEL[curStatus] || "text-slate-400"}`}>
          {allDone ? "All stages done" : `${curStatus === "stuck" ? "Stuck at " : ""}${name(cur)}`}
        </span>
        {!allDone && (
          <span className="text-slate-500"> · step {idx + 1} of {stages.length}</span>
        )}
      </div>
    </div>
  );
}
